/**
 * Helper functions to get multilingual content from webTemplate
 * based on the current language, with fallback to defaultLanguage
 */

/**
 * ISO-ish codes: "es-ES" → "es" for comparisons and ML map lookups
 */
export function normalizeLangCode(code) {
	if (code == null || code === "") return "es";
	return String(code).split("-")[0].toLowerCase();
}

function pickFromMlObject(mlField, language) {
	if (!mlField || typeof mlField !== "object") return "";
	const keys = Object.keys(mlField);
	if (keys.length === 0) return "";
	const raw = String(language);
	if (mlField[raw]) return String(mlField[raw]);
	const n = normalizeLangCode(raw);
	if (mlField[n]) return String(mlField[n]);
	for (const k of keys) {
		if (normalizeLangCode(k) === n && mlField[k]) return String(mlField[k]);
	}
	return "";
}

/**
 * Get a multilingual field value from webTemplate (or any object with `field` + `field_ml`)
 *
 * - If `field_ml` exists, uses the value for the **current** language only.
 * - If there is no entry for the current language, falls back only when
 *   current === default: then the default language key in _ml, then legacy `field`
 *   (treated as written in the site default language). No "first random language"
 *   fallback so missing `ca`/`en` does not show Spanish.
 * - If there is no `_ml` object, the legacy `field` string is returned only when
 *   `normalize(currentLanguage) === normalize(defaultLanguage)`; otherwise "" so
 *   templates can use `t()` fallbacks.
 */
export function getMLField(
	webTemplate,
	fieldName,
	currentLanguage,
	defaultLanguage = "es"
) {
	if (!webTemplate) return "";
	const curNorm = normalizeLangCode(currentLanguage);
	const defNorm = normalizeLangCode(defaultLanguage);
	const mlFieldName = `${fieldName}_ml`;
	let mlField = webTemplate[mlFieldName];
	if (typeof mlField === "string") {
		try {
			mlField = JSON.parse(mlField);
		} catch {
			mlField = null;
		}
	}
	const hasMl =
		mlField && typeof mlField === "object" && Object.keys(mlField).length > 0;

	if (hasMl) {
		const forCurrent = pickFromMlObject(mlField, currentLanguage);
		if (forCurrent) return forCurrent;
		if (curNorm === defNorm) {
			const forDef = pickFromMlObject(mlField, defaultLanguage);
			if (forDef) return forDef;
		}
		return "";
	}

	const flat = webTemplate[fieldName];
	if (flat != null && String(flat).trim() && curNorm === defNorm) {
		return String(flat);
	}
	return "";
}

/**
 * Get the title from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The title in the current language or default
 */
export function getTitle(webTemplate, currentLanguage, defaultLanguage = "es") {
	return getMLField(webTemplate, "title", currentLanguage, defaultLanguage);
}

/**
 * Get the description from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The description in the current language or default
 */
export function getDescription(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"description",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the heroTitle from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The heroTitle in the current language or default
 */
export function getHeroTitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(webTemplate, "heroTitle", currentLanguage, defaultLanguage);
}

/**
 * Get the heroSubtitle from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The heroSubtitle in the current language or default
 */
export function getHeroSubtitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"heroSubtitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the aboutTitle from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The aboutTitle in the current language or default
 */
export function getAboutTitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"aboutTitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the aboutDescription from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The aboutDescription in the current language or default
 */
export function getAboutDescription(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"aboutDescription",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the menuTitle from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The menuTitle in the current language or default
 */
export function getMenuTitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(webTemplate, "menuTitle", currentLanguage, defaultLanguage);
}

/**
 * Get the menuSubtitle from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The menuSubtitle in the current language or default
 */
export function getMenuSubtitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"menuSubtitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the contactTitle from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The contactTitle in the current language or default
 */
export function getContactTitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"contactTitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the contactSubtitle from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The contactSubtitle in the current language or default
 */
export function getContactSubtitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"contactSubtitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the reservationTitle from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The reservationTitle in the current language or default
 */
export function getReservationTitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"reservationTitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the reservationSubtitle from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The reservationSubtitle in the current language or default
 */
export function getReservationSubtitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"reservationSubtitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the navInicio from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The navInicio in the current language or default
 */
export function getNavInicio(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(webTemplate, "navInicio", currentLanguage, defaultLanguage);
}

/**
 * Get the navAbout from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The navAbout in the current language or default
 */
export function getNavAbout(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(webTemplate, "navAbout", currentLanguage, defaultLanguage);
}

/**
 * Get the navMenu from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The navAbout in the current language or default
 */
export function getNavMenu(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(webTemplate, "navMenu", currentLanguage, defaultLanguage);
}

/**
 * Get the navReserva from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The navReserva in the current language or default
 */
export function getNavReserva(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"navReserva",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the navContacto from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The navContacto in the current language or default
 */
export function getNavContacto(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"navContacto",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the navPedidos from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The navPedidos in the current language or default
 */
export function getNavPedidos(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"navPedidos",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the heroButton1 from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The heroButton1 in the current language or default
 */
export function getHeroButton1(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"heroButton1",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the heroButton2 from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The heroButton2 in the current language or default
 */
export function getHeroButton2(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"heroButton2",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the menuButton from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The menuButton in the current language or default
 */
export function getMenuButton(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"menuButton",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the serviceSectionTitle from webTemplate based on current language
 */
export function getServiceSectionTitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"serviceSectionTitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the serviceSectionSubtitle from webTemplate based on current language
 */
export function getServiceSectionSubtitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"serviceSectionSubtitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the navServicios from webTemplate based on current language
 */
export function getNavServicios(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"navServicios",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the specialtySectionTitle from webTemplate based on current language
 */
export function getSpecialtySectionTitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"specialtySectionTitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the specialtySectionSubtitle from webTemplate based on current language
 */
export function getSpecialtySectionSubtitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"specialtySectionSubtitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the navEspecialidades from webTemplate based on current language
 */
export function getNavEspecialidades(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"navEspecialidades",
		currentLanguage,
		defaultLanguage
	);
}

export function getCustomCatalogTitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"customCatalogTitle",
		currentLanguage,
		defaultLanguage
	);
}

export function getCustomCatalogSubtitle(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"customCatalogSubtitle",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the contactSchedule from webTemplate based on current language
 * @param {Object} webTemplate - WebTemplate object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The contactSchedule in the current language or default
 */
export function getContactSchedule(
	webTemplate,
	currentLanguage,
	defaultLanguage = "es"
) {
	return getMLField(
		webTemplate,
		"contactSchedule",
		currentLanguage,
		defaultLanguage
	);
}

/**
 * Get the title of a product or service based on current language
 * @param {Object} item - Product or service object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The title in the current language or default
 */
export function getProductTitle(item, currentLanguage, defaultLanguage = "es") {
	if (!item) return "";
	const actualItem = item.dataValues || item;
	let titleML = actualItem.title_ml;
	if (titleML && typeof titleML === "string") {
		try {
			titleML = JSON.parse(titleML);
		} catch (e) {
			// mantener string → getMLField ignora _ml inválido
		}
	}
	const o =
		typeof titleML === "object" && titleML !== null
			? { ...actualItem, title_ml: titleML }
			: actualItem;
	const result = getMLField(o, "title", currentLanguage, defaultLanguage);
	if (result) return result;
	// Fallback: try default language, then flat field
	if (titleML && typeof titleML === "object") {
		const val = pickFromMlObject(titleML, defaultLanguage)
			|| Object.entries(titleML).find(([k, v]) => v)?.[1]
			|| "";
		if (val) return val;
	}
	return actualItem.title || "";
}

/**
 * Get the description of a product or service based on current language
 * @param {Object} item - Product or service object
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The description in the current language or default
 */
export function getProductDescription(
	item,
	currentLanguage,
	defaultLanguage = "es"
) {
	if (!item) return "";
	const actualItem = item.dataValues || item;
	let descriptionML = actualItem.description_ml;
	if (descriptionML && typeof descriptionML === "string") {
		try {
			descriptionML = JSON.parse(descriptionML);
		} catch (e) {
			// leave as string
		}
	}
	const o =
		typeof descriptionML === "object" && descriptionML !== null
			? { ...actualItem, description_ml: descriptionML }
			: actualItem;
	const result = getMLField(o, "description", currentLanguage, defaultLanguage);
	if (result) return result;
	// Fallback: try default language, then flat field
	if (descriptionML && typeof descriptionML === "object") {
		const val = pickFromMlObject(descriptionML, defaultLanguage)
			|| Object.entries(descriptionML).find(([k, v]) => v)?.[1]
			|| "";
		if (val) return val;
	}
	return actualItem.description || "";
}

/**
 * Get the name of an item (like quick actions) based on current language
 * @param {Object} item - Item object with name_ml field
 * @param {string} currentLanguage - Current language code (e.g., 'es', 'ca')
 * @param {string} defaultLanguage - Default language code for fallback
 * @returns {string} - The name in the current language or default
 */
export function getName(item, currentLanguage, defaultLanguage = "es") {
	if (!item) return "";

	// Normalize currentLanguage (handle cases like "ca-ES" -> "ca")
	const langCode = currentLanguage?.split("-")[0] || currentLanguage || "es";

	// Handle Sequelize dataValues wrapper
	const actualItem = item.dataValues || item;

	// Try to get from multilingual field
	let nameML = actualItem.name_ml;

	// Handle stringified JSON
	if (nameML && typeof nameML === "string") {
		try {
			nameML = JSON.parse(nameML);
		} catch (e) {
			// If parsing fails, treat as regular string
		}
	}

	if (nameML && typeof nameML === "object" && nameML !== null) {
		// Try current language first (with and without locale)
		if (nameML[langCode]) {
			return nameML[langCode];
		}
		if (nameML[currentLanguage]) {
			return nameML[currentLanguage];
		}
		// Fallback to default language
		if (nameML[defaultLanguage]) {
			return nameML[defaultLanguage];
		}
		// Fallback to first available language
		const firstLang = Object.keys(nameML)[0];
		if (firstLang && nameML[firstLang]) {
			return nameML[firstLang];
		}
		// If name_ml exists but is empty object, fallback to regular name
		if (Object.keys(nameML).length === 0) {
			return actualItem.name || "";
		}
	}

	// Fallback to regular name field
	return actualItem.name || "";
}
